import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useUserContext } from '../config/UserContext';
import { useKeycloak } from '../config/KeycloakContext';
import type { Document } from '../../../shared/types/models/document';
import { DOCUMENT_TYPE } from '../../../shared/types/models/document';
import type { Entity } from '../../../shared/types/models/entity';
import EntityListComponent, { getEntityStatus } from '../components/EntityListComponent';

const FreezeResultsPage: React.FC = () => {
  const { freezeId: freezeLookup } = useParams<{ freezeId: string }>();
  const { freeze: contextFreeze, setFreeze: setContextFreeze, apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError } = useUserContext();
  const { getToken } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freeze, setFreeze] = useState<any>(contextFreeze);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [entitiesByDoc, setEntitiesByDoc] = useState<Record<string, { loading: boolean; error: string | null; entities: Entity[] }>>({});
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});

  const navigate = useNavigate();
  const [createDocLoading, setCreateDocLoading] = useState(false);

  useEffect(() => {
    if (!freezeLookup || !apiBaseUrl) return;

    const fetchFreeze = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freezeLookup}`, { headers, cache: 'no-store' });
        if (res.status === 401) throw new Error('Unauthorized: Please log in');
        if (!res.ok) throw new Error('Failed to fetch freeze info');
        const data = await res.json();
        console.log('[FreezeResultsPage] Freeze fetch result (with county join):', data);
        setFreeze(data.data);
        setContextFreeze(data.data);
      } catch (err: any) {
        console.error('[FreezeResultsPage] Freeze fetch error:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    fetchFreeze();
  }, [freezeLookup, apiBaseUrl, getToken]);

  useEffect(() => {
    if (!freezeLookup || freezeLookup === 'new') return;
    let isMounted = true;
    setDocumentsLoading(true);
    setDocumentsError(null);
    (async () => {
      try {
        const token = await getToken();
        if (!token) {
          if (isMounted) setDocumentsError('Not authenticated');
          setDocumentsLoading(false);
          return;
        }
        const res = await fetch(`${apiBaseUrl}/api/frozendeed/documents?freezeLookup=${freezeLookup}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store'
        });
        if (!res.ok) throw new Error('Failed to fetch documents');
        const data = await res.json();
        console.log('[FreezeResultsPage] Documents fetch result:', data);
        if (isMounted) setDocuments(Array.isArray(data.data) ? data.data : []);
      } catch (err: any) {
        console.error('[FreezeResultsPage] Documents fetch error:', err);
        if (isMounted) setDocumentsError(err.message || 'Unknown error');
      } finally {
        if (isMounted) setDocumentsLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [freezeLookup, apiBaseUrl, getToken]);

  // Fetch entities for each document
  useEffect(() => {
    if (!documents.length) return;
    documents.forEach((doc: Document) => {
      if (entitiesByDoc[doc.lookup]) return; // already fetched
      setEntitiesByDoc(prev => ({ ...prev, [doc.lookup]: { loading: true, error: null, entities: [] } }));
      (async () => {
        try {
          const token = await getToken();
          if (!token) {
            setEntitiesByDoc(prev => ({ ...prev, [doc.lookup]: { loading: false, error: 'Not authenticated', entities: [] } }));
            return;
          }
          const res = await fetch(`${apiBaseUrl}/api/frozendeed/entities?documentLookup=${doc.lookup}`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store'
          });
          if (!res.ok) throw new Error('Failed to fetch entities');
          const data = await res.json();
          setEntitiesByDoc(prev => ({ ...prev, [doc.lookup]: { loading: false, error: null, entities: Array.isArray(data.data) ? data.data : [] } }));
        } catch (err: any) {
          setEntitiesByDoc(prev => ({ ...prev, [doc.lookup]: { loading: false, error: err.message || 'Unknown error', entities: [] } }));
        }
      })();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, apiBaseUrl, getToken]);

  // Debug: final state before rendering
  console.log('[FreezeResultsPage] Final state:', { freeze, documents, loading, error, documentsLoading, documentsError });

  if (apiBaseUrlLoading) return <div>Loading configuration...</div>;
  if (apiBaseUrlError) return <div>Error loading configuration: {apiBaseUrlError}</div>;
  if (loading) return <div>Loading freeze info...</div>;
  if (error) return <div className="text-red-500">{error}</div>;
  if (!freeze) return <div>No freeze info found.</div>;

  if (documentsLoading) return <div>Loading documents...</div>;
  if (documentsError) return <div className="text-red-500">{documentsError}</div>;

  const handleCreateDocument = async () => {
    setCreateDocLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/api/frozendeed/document/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ freezeLookup: freeze.lookup, type: DOCUMENT_TYPE.LIEN })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to create document');
      }
      const data = await res.json();
      if (data?.data) {
        // Navigate to /start — FlowPage will auto-route
        navigate(`/document/${data.data.lookup}/start`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create document');
    } finally {
      setCreateDocLoading(false);
    }
  };

  const firstDeed = freeze?.countyIndexes?.[0];
  const firstParcel = freeze?.parcels?.[0];
  const primaryRecord = (firstDeed?.street1 || firstDeed?.address || firstDeed?.legalDescription) ? firstDeed : (firstParcel || firstDeed);

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-lg p-8 mt-8">
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-[26px] font-plus-jakarta font-bold text-[#141435]">Property Details</h2>
        {freeze.search && (
          <p className="text-sm text-gray-500 mt-1 font-plus-jakarta">{freeze.search}</p>
        )}
      </div>
      <div className="border rounded-lg p-6 bg-white shadow-sm w-full mt-4 mb-6">

        {/* Dynamic Recording Indexes display based on documentSchema */}
        {freeze.county?.documentSchema?.indexes?.map((idx: any, i: number) => {
          const val = freeze.recordingIndexes?.[idx.key];
          return (
            <div key={idx.key} className={`mb-5 ${i > 0 ? 'border-t pt-4' : ''}`}>
              <span className="text-[13px] font-plus-jakarta font-semibold text-gray-400 uppercase tracking-wide block mb-1">
                {idx.label}
              </span>
              <span className="text-[16px] font-plus-jakarta font-semibold text-[#141435]">
                {val || <span className="text-gray-300 italic font-normal">Not set</span>}
              </span>
            </div>
          );
        })}
        {(!freeze.county?.documentSchema?.indexes || freeze.county.documentSchema.indexes.length === 0) && (
           <div className="mb-5">
             <span className="text-gray-500 italic">No property indexes configured for this county.</span>
           </div>
        )}

        {/* Address Section */}
        <div className="mb-6 border-t pt-5">
          <div className="flex justify-between items-center mb-3">
            <span className="text-[16px] font-plus-jakarta font-bold text-[#141435]">Property Address</span>
          </div>
          <div className="text-[15px] text-gray-700 bg-gray-50 p-4 rounded-md border">
            {primaryRecord?.address || (
              <>
                {primaryRecord?.street1 || ''}{primaryRecord?.street2 ? ' ' + primaryRecord.street2 : ''}
                {primaryRecord?.street1 || primaryRecord?.street2 ? <br /> : ''}
                {[primaryRecord?.city, [freeze.state?.abbr, primaryRecord?.zipcode].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
              </>
            )}
          </div>
        </div>

        {/* Legal Description Section */}
        <div className="border-t pt-5">
          <div className="flex justify-between items-center mb-3">
            <span className="text-[16px] font-plus-jakarta font-bold text-[#141435]">
              {freeze.county?.documentSchema?.legalDescriptionLabel || 'Legal Description'}
            </span>
          </div>
          <div className="text-[15px] text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-md border min-h-[80px]">
            {primaryRecord?.legalDescription || ''}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end mb-6 gap-2">
        <button
          className={`font-plus-jakarta font-bold py-2.5 px-6 rounded-full flex items-center gap-2 transition-colors ${
            createDocLoading
              ? 'bg-[#0B4BB6] opacity-40 cursor-not-allowed text-white'
              : 'bg-[#0B4BB6] hover:bg-[#23235a] text-white'
          }`}
          onClick={handleCreateDocument}
          disabled={createDocLoading}
        >
          {createDocLoading ? 'Creating...' : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
              </svg>
              Create Frozen Deed
            </>
          )}
        </button>
      </div>

      {/* Frozen Deeds on this Property */}
      <h3 className="text-[18px] font-plus-jakarta font-bold text-[#141435] mb-4">Frozen Deeds on this Property</h3>
      {documents.filter(d => d.type === DOCUMENT_TYPE.LIEN).length === 0 && (
        <div className="mb-6 text-gray-500">No frozen deeds have been created for this property yet.</div>
      )}
      {documents.filter(d => d.type === DOCUMENT_TYPE.LIEN).map((doc) => {
        const isExpanded = expandedDocs[doc.lookup];
        const isReleased = documents.some(
          d => d.type === DOCUMENT_TYPE.LIEN_RELEASE && 
          (d.parentReference && d.parentReference === doc.frozenDeedReference) && 
          d.generated
        );
        let statusText = 'Draft';
        if (isReleased) statusText = 'Released';
        else if (doc.filedDate) statusText = 'Filed';
        else if (doc.generated) statusText = 'Active';

        return (
          <div key={doc.lookup} className="mb-6 flex flex-col border rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-3">
                <div className="font-bold text-base">
                  Freeze #{doc.frozenDeedReference ? ` (${doc.frozenDeedReference})` : ''}
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  isReleased ? 'bg-gray-200 text-gray-700' : 
                  statusText === 'Filed' ? 'bg-blue-100 text-blue-700' :
                  doc.generated ? 'bg-green-100 text-green-700' : 
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {statusText}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-4 py-1.5 text-sm font-plus-jakarta font-semibold text-[#0B4BB6] border border-[#0B4BB6] rounded hover:bg-[#0B4BB6] hover:text-white transition-colors"
                  style={{ borderRadius: 4 }}
                  onClick={() => {
                    if (doc.generated) {
                      navigate(`/document/${doc.lookup}/details`);
                    } else {
                      // Navigate to /start — FlowPage will auto-route
                      navigate(`/document/${doc.lookup}/start`);
                    }
                  }}
                >
                  {doc.generated ? 'View Details' : 'Resume Draft'}
                </button>
                {/* Expand/Collapse toggle button */}
                <button
                  className="w-8 h-8 flex items-center justify-center text-[#485D7D] focus:outline-none"
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  onClick={() => setExpandedDocs(prev => ({ ...prev, [doc.lookup]: !prev[doc.lookup] }))}
                >
                  {isExpanded ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7"/></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 15l7-7 7 7"/></svg>
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 font-semibold">{doc.referenceTo}</div>
            </div>
            {!isExpanded && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {entitiesByDoc[doc.lookup]?.loading && <span className="text-xs text-gray-400">Loading entities...</span>}
                {entitiesByDoc[doc.lookup]?.error && <span className="text-xs text-red-500">{entitiesByDoc[doc.lookup]?.error}</span>}
                {entitiesByDoc[doc.lookup]?.entities && entitiesByDoc[doc.lookup].entities.length > 0 &&
                  entitiesByDoc[doc.lookup].entities.map((entity: Entity) => {
                    const m: any = entity.metadata || {};
                    const status = getEntityStatus(entity);
                    return (
                      <span
                        key={entity.lookup}
                        className={`flex items-center gap-1 border rounded px-2 py-1 text-xs font-plus-jakarta font-semibold ${status === 'warning' ? 'bg-[#FFFBEA] border-[#FFD600] text-[#8A6D1B]' : 'bg-[#EEFFEE] border-[#92C892] text-[#3C7F3C]'}`}
                        style={{ borderRadius: 4 }}
                      >
                        {entity.type.charAt(0).toUpperCase() + entity.type.slice(1)}
                        {m?.firstName && ' - ' + m.firstName}
                        {m?.lastName && ' ' + m.lastName}
                        {m?.name && ' - ' + m.name}
                      </span>
                    );
                  })
                }
                {entitiesByDoc[doc.lookup] && !entitiesByDoc[doc.lookup].loading && entitiesByDoc[doc.lookup].entities.length === 0 && !entitiesByDoc[doc.lookup].error && (
                  <span className="text-xs text-gray-400">No entities</span>
                )}
              </div>
            )}
            {isExpanded && (
              <div className="mt-3 flex flex-col items-center w-full">
                {entitiesByDoc[doc.lookup]?.loading && <span className="text-xs text-gray-400">Loading entities...</span>}
                {entitiesByDoc[doc.lookup]?.error && <span className="text-xs text-red-500">{entitiesByDoc[doc.lookup]?.error}</span>}
                {entitiesByDoc[doc.lookup]?.entities && entitiesByDoc[doc.lookup].entities.length > 0 && (
                  <EntityListComponent
                    entities={entitiesByDoc[doc.lookup].entities}
                    step="summary"
                    onOpenModal={() => {}}
                    modalOpen={null}
                    showStatus={true}
                  />
                )}
                {entitiesByDoc[doc.lookup] && !entitiesByDoc[doc.lookup].loading && entitiesByDoc[doc.lookup].entities.length === 0 && !entitiesByDoc[doc.lookup].error && (
                  <span className="text-xs text-gray-400">No entities</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FreezeResultsPage;
